import React from 'react';
import './DateRangePicker.css';
import { DateRangePickerState, FixedDateRange, DateModes } from '../../utils/types';
import { Accordion, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import generateDateRanges from '../../utils/generateDateRanges';
import dayjs from "../../utils/dayjs";

function MaxRangeTooltip(props:any) {
  return (
    <OverlayTrigger
      key="dates-tooltip-overlay"
      placement={props.placement}
      overlay={( props ) => {
        return (                        
          <Tooltip {...props} id="dates-tooltip">
            <div>Maximum of 3 months allowed. Use the months mode to report on longer periods.</div>
          </Tooltip>
        );
      }}>
      {props.slotElement}
    </OverlayTrigger>
  );
}

interface DayProps {
  key:string;
  day:dayjs.Dayjs;
  dateStart:dayjs.Dayjs;
  dateEnd:dayjs.Dayjs;
  hover:dayjs.Dayjs;
  otherMonth:boolean;
  isFirstClickInDateRange:boolean;
  future:boolean;
  today:boolean;
  handleDayClick: (day:dayjs.Dayjs) => void;
  setHover: (hover:boolean, day?:dayjs.Dayjs) => void; 
  selectWeeks: boolean;
}

function Day(props:DayProps) {
  let hoverStart = null, hoverEnd = null;
  const day = dayjs.utc(props.day);
  if (props.hover) {
    hoverStart = !props.hover.isAfter(props.dateEnd) ? props.hover : props.dateStart;
    hoverEnd = !props.hover.isBefore(props.dateStart) ? props.hover : props.dateEnd;

    if ( props.selectWeeks ) {
      hoverStart = hoverStart.startOf("isoWeek");
      hoverEnd = hoverEnd.endOf("isoWeek");
    }
  } 

  const isDaySelected = !props.otherMonth && !day.isBefore(props.dateStart, "year") && !day.isAfter(props.dateEnd, "year") &&
    !day.isBefore(props.dateStart, "month") && !day.isAfter(props.dateEnd, "month") &&
    !day.isBefore(props.dateStart, "day") && !day.isAfter(props.dateEnd, "day");
  const isOutOfRange = !props.isFirstClickInDateRange && (day.isBefore(dayjs.utc(props.dateStart).subtract(3, "months")) || day.isAfter(dayjs.utc(props.dateEnd).add(3, "months")));
  const isDayHovered = !props.otherMonth && !isOutOfRange && props.hover && !day.isBefore(hoverStart, "year") && !day.isAfter(hoverEnd, "year") &&
      !day.isBefore(hoverStart, "month") && !day.isAfter(hoverEnd, "month") &&
      !day.isBefore(hoverStart, "day") && !day.isAfter(hoverEnd, "day");

  const isStartOrEnd = !props.otherMonth && (day.isSame(props.dateStart) || day.isSame(props.dateEnd));
  const isStartOrEndHover = !props.otherMonth && !isOutOfRange && day.isSame(props.hover);

  let dateClicked:dayjs.Dayjs;
  if ( props.selectWeeks ) {
    dateClicked = props.isFirstClickInDateRange ? day.startOf("isoWeek") : day.endOf("isoWeek");
  } else {
    dateClicked = day;
  }

  const dayElement:any = (
    <span className={`day ${isDaySelected ? "selected" : ""} ${((isDayHovered || isStartOrEndHover) && !props.isFirstClickInDateRange && !props.future) ? "hovered" : ""} ${props.future ? "future" : ""} ${props.today ? "today" : ""} ${props.otherMonth || isOutOfRange ? "othermonth" : ""} ${isStartOrEnd ? "start-end" : ""}`} 
      onClick={props.future || props.otherMonth || isOutOfRange ? null : () => props.handleDayClick(dateClicked)}
      onMouseEnter={() => props.setHover(true, day)}
      onMouseLeave={() => props.setHover(false, day)}
    >
      {dayjs.utc(day).format("D")}
    </span>
  );

  return (
    isOutOfRange && !props.future ? (
      <MaxRangeTooltip
        placement={day.isBefore(dayjs.utc(props.dateStart)) ? "left-end" : "right-start"}
        slotElement={dayElement}
      />
    ) : (
      <>{dayElement}</>
    )
  );
}

interface WeekNumberProps {
  key:string;
  week:dayjs.Dayjs;
  dateStart:dayjs.Dayjs;
  dateEnd:dayjs.Dayjs;
  hover:dayjs.Dayjs;
  otherMonth:boolean;
  isFirstClickInDateRange:boolean;
  future:boolean;
  today:boolean;
  handleWeekClick: (day:dayjs.Dayjs) => void;
  setHover: (hover:boolean, day?:dayjs.Dayjs) => void;  
}

function WeekNumber(props:WeekNumberProps) {
  const startOfWeek = props.week;
  const endOfWeek = props.week.endOf("isoWeek");

  const dayElement:any = (
    <span className={"weekNumber"}
      onClick={() => props.handleWeekClick(props.isFirstClickInDateRange ? startOfWeek : endOfWeek)}
      onMouseEnter={() => props.setHover(true, props.isFirstClickInDateRange ? startOfWeek : endOfWeek)}
      onMouseLeave={() => props.setHover(false, props.isFirstClickInDateRange ? startOfWeek : endOfWeek)}
    >
      {dayjs.utc(props.week).isoWeek()}
    </span>
  );

  return (
      <>{dayElement}</>
  );
}

interface WeekProps {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  monthStart: dayjs.Dayjs;
  monthEnd: dayjs.Dayjs;
  dateStart: dayjs.Dayjs;
  dateEnd: dayjs.Dayjs;

  hover: dayjs.Dayjs;
  setHover: (hover:boolean, date:dayjs.Dayjs) => void;

  isFirstClickInDateRange: boolean;
  selectWeeks: boolean;
  
  handleDayClick: (date:dayjs.Dayjs)=>void;
}

function Week(props:WeekProps) {
  const today = dayjs.utc().startOf("day");
  const days = [];
  const diff = props.end.diff(props.start, "days");

  if ( props.selectWeeks ) {
    days.push(
      <WeekNumber 
        key={props.start.isoWeekYear + "W" + props.start.isoWeek()}
        week={props.start.startOf("isoWeek")}
        setHover={props.setHover}
        isFirstClickInDateRange={props.isFirstClickInDateRange}
        hover={props.hover}
        today={false} 
        future={false}
        otherMonth={false}
        dateStart={null}
        dateEnd={null}
        handleWeekClick={(date:dayjs.Dayjs)=>props.handleDayClick(date)}
      />
    );
  }

  for (let i = 0; i < diff; i++) {
    const day = dayjs.utc(props.start).add(i, "days");

    days.push(
      <Day 
        key={day.toLocaleString()} 
        day={day}
        setHover={props.setHover}
        isFirstClickInDateRange={props.isFirstClickInDateRange}
        hover={props.hover}
        today={day.isSame(today)} 
        future={day.isAfter(today)}
        otherMonth={day.isBefore(props.monthStart) || day.isAfter(props.monthEnd)}
        dateStart={props.dateStart}
        dateEnd={props.dateEnd}
        handleDayClick={(date:dayjs.Dayjs)=>props.handleDayClick(date)}
        selectWeeks={props.selectWeeks}
      />
    );
  }

  return <div className="week">{days}</div>;
}

function Month(props:any) {
  let hoverStart = null;
  if (props.hover) {
    hoverStart = !props.hover.isAfter(props.dateEnd, "month") ? props.hover : props.dateStart;
  }
  const hoverEnd = props.hover ? (!props.hover.isBefore(props.dateStart, "month") ? props.hover : props.dateEnd) : null;
  const isMonthHovered = props.hover && !props.month.isBefore(hoverStart, "year") && !props.month.isAfter(hoverEnd, "year") &&
    !props.month.isBefore(hoverStart, "month") && !props.month.isAfter(hoverEnd, "month"); 

  const isMonthSelected = !props.month.isBefore(props.dateStart, "year") && !props.month.isAfter(props.dateEnd, "year") &&
    !props.month.isBefore(props.dateStart, "month") && !props.month.isAfter(props.dateEnd, "month");

  const isStartOrEnd = props.month.isSame(props.dateStart, "month") || props.month.isSame(props.dateEnd, "month");
  const isStartOrEndHover = props.month.isSame(props.hover, "month");

  return <span className={`month ${isMonthSelected ? "selected" : ""} ${props.future ? "future" : ""} ${props.today ? "today" : ""} ${isStartOrEnd ? "start-end" : ""}
    ${((isMonthHovered || isStartOrEndHover) && !props.isFirstClickInDateRange && !props.future) ? "hovered" : ""}`}
    onMouseEnter={() => props.setHover(true, props.month)}
    onMouseLeave={() => props.setHover(false, props.month)}
    onClick={props.future ? null : () => props.handleMonthClick(props.month)}
  >
    {dayjs.utc(props.month).format("MMM").toUpperCase()}
  </span>
}

interface MonthCalendarProps {
  id:string;
  isFirstClickInDateRange:boolean;
  hover:dayjs.Dayjs;
  dateStart:dayjs.Dayjs;
  dateEnd:dayjs.Dayjs;
  year:dayjs.Dayjs;

  minDate?:dayjs.Dayjs;
  maxDate?:dayjs.Dayjs;

  setHover:(enableHover:boolean, date:dayjs.Dayjs) => void;
  prevMonthOrYear?:()=>void;
  nextMonthOrYear?:()=>void;
  handleMonthClick:(dateStart:dayjs.Dayjs, dateEnd:dayjs.Dayjs)=>void;
}

function MonthCalendar(props:MonthCalendarProps) {
  const months = [];
  const today = dayjs.utc().startOf("day");

  for (let i = 0; i < 12; i++) {
    const month = dayjs.utc(props.year).month(i);
    months.push(
      <Month 
        key={month.toLocaleString()} 
        month={month}
        setHover={props.setHover}
        hover={props.hover}
        isFirstClickInDateRange={props.isFirstClickInDateRange}
        dateStart={props.dateStart}
        dateEnd={props.dateEnd}
        future={month.isAfter(today, "month")}
        handleMonthClick={(date:any)=>{
          const dateEnd1 = dayjs.utc(date).endOf("month").startOf("day");
          const dateEnd2 = dayjs.utc(props.dateStart).endOf("month").startOf("day");
          if (props.isFirstClickInDateRange) {
            return props.handleMonthClick(date, dateEnd1);
          } else if (!date.isBefore(props.dateStart)) {
            return props.handleMonthClick(props.dateStart, dateEnd1);
          } else {
            return props.handleMonthClick(date, dateEnd2);
          }
        }}
      />
    );
  }

  return <div className="calendar">
    <div className="yearname">
      <div className="calendar-header calendar-header-left">
        {props.prevMonthOrYear && props.minDate && props.year.isAfter(props.minDate) &&
          <div className="button-container" onClick={() => props.prevMonthOrYear()}>
            <span className="button button-prev"></span>
          </div>
        }
      </div>
      <div className="calendar-header calendar-header-center">
        {props.year.format("YYYY")}
      </div>
      <div className="calendar-header calendar-header-right">
        {props.nextMonthOrYear && props.maxDate && props.year.isBefore(props.maxDate) &&
          <div className="button-container" onClick={() => props.nextMonthOrYear()}>
            <span className="button button-next"></span>
          </div>
        }
      </div>
    </div>
    <div className="months">
      {months}
    </div>
  </div>
}

interface DayCalendarProps {
  id:string,
  key:string,
  month:dayjs.Dayjs,
  dateStart:dayjs.Dayjs,
  dateEnd:dayjs.Dayjs,
  maxDate?:dayjs.Dayjs,
  minDate?:dayjs.Dayjs,
  selectWeeks:boolean;

  isFirstClickInDateRange:boolean,

  hover: dayjs.Dayjs;
  setHover: (hover:boolean, date:dayjs.Dayjs) => void;

  nextMonthOrYear?:()=>void,
  prevMonthOrYear?:()=>void,
  handleDayClick:(date:dayjs.Dayjs)=>void,
}

function DayCalendar(props:DayCalendarProps) {
  const som = dayjs.utc(props.month).startOf("isoWeek");
  const eom = dayjs.utc(props.month).endOf("month").startOf("day");

  // days of the week start on Monday for iso8601 weeks, regardless of Locale
  const dayNames = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map( name => <div className="day dayofweek" key={name}>{name}</div> );

  const weeks = [
    <div key="week-dayNames" className="week dayNames">
      {props.selectWeeks ? <span className="weekNumber">Wk</span> : null}
      {dayNames}
    </div>
  ];

  for (let sow = dayjs.utc(som); sow.isSame(eom) || sow.isBefore(eom); sow = dayjs.utc(sow).add(7, "days")) {
    const eow = dayjs.utc(sow).add(7, "days");
    weeks.push(
      <Week 
        key={`week-${sow.toLocaleString()}`} 
        start={sow} 
        end={eow}
        setHover={props.setHover}
        isFirstClickInDateRange={props.isFirstClickInDateRange}
        hover={props.hover}
        dateStart={props.dateStart}
        dateEnd={props.dateEnd}
        monthStart={dayjs.utc(props.month).startOf("month")}
        monthEnd={dayjs.utc(props.month).endOf("month").startOf("day")}
        handleDayClick={(date:any)=>props.handleDayClick(date)}
        selectWeeks={props.selectWeeks}
      />
    )
  }

  return <div className="calendar">
    <div className="monthname">
      <div className="calendar-header calendar-header-left">
        {
          (props.prevMonthOrYear && props.minDate && !(dayjs.utc(props.month).isSame(props.minDate) || dayjs.utc(props.month).isBefore(props.minDate))) && (
            !props.isFirstClickInDateRange && (props.month.isBefore(dayjs.utc(props.dateStart).subtract(3, "months"))) ? (
              <MaxRangeTooltip
                placement="left"
                slotElement={
                  (<div className="button-container disabled" >
                    <span className="button button-prev disabled"></span>
                  </div>)
                }
              />
            ) : (
              <div className="button-container" onClick={() => props.prevMonthOrYear()}>
                <span className="button button-prev"></span>
              </div>
            )
          )
        }
      </div>
      <div className="calendar-header calendar-header-center">
        {props.month.format("YYYY")}-{props.month.format("MM")} ({props.month.format("MMM")})
      </div>
      <div className="calendar-header calendar-header-right">
        {
          (props.nextMonthOrYear && props.maxDate && props.month.isBefore(props.maxDate)) && (
            !props.isFirstClickInDateRange && (props.month.isAfter(dayjs.utc(props.dateEnd).add(3, "months"))) ? (
              <MaxRangeTooltip
                placement="right"
                slotElement={
                  (<div className="button-container disabled" >
                    <span className="button button-next disabled"></span>
                  </div>)
                }
              />
            ) : (
              <div className="button-container" onClick={() => props.nextMonthOrYear()}>
                <span className="button button-next"></span>
              </div>
            )
          )
        }
      </div>
    </div>
    <div className="weeks">
      {weeks}
    </div>
  </div>
}

interface DateRangePickerProps {
  minDate:string|dayjs.Dayjs;
  maxDate?:string|dayjs.Dayjs;
  startDate:string|dayjs.Dayjs;
  endDate:string|dayjs.Dayjs;
  onClose: any;
  onRangeSelect: (start : dayjs.Dayjs,end: dayjs.Dayjs, dateMode: DateModes) => void;
  onClickOutside: any;
  dateMode: DateModes;
}

function DateRangePicker(props:DateRangePickerProps) {
  const ref = React.useRef(null);
  const { onClickOutside, onRangeSelect, onClose } = props;

  const initalState:DateRangePickerState = {
    ...generateDateRanges(props.startDate, props.endDate, props.minDate, props.maxDate),
    previousDateStart: null,
    previousDateEnd: null,
    isFirstClickInDateRange: true,
    hover: null,
    dateMode: props.dateMode,
  };

  const [state,setState] = React.useState<DateRangePickerState>(initalState);

  function handleFixedRangeClick(dateMode: DateModes, range:FixedDateRange) {
    setState({
      ...state,
      month1: dayjs.utc().startOf("day").subtract(1, "month").startOf("month"),
      month2: dayjs.utc().startOf("day").startOf("month"),
      dateStart: range[0],
      dateEnd: range[1],
      isFirstClickInDateRange: true,
      dateMode: dateMode
    });


    if (onRangeSelect) {
      onRangeSelect(range[0], range[1], dateMode);
      onClose();
    }
  }

  function handleMonthClick(dateStart:dayjs.Dayjs, dateEnd:dayjs.Dayjs) {
    setState({
      ...state,
      dateStart: dateStart,
      dateEnd: dateEnd,
      previousDateStart: state.isFirstClickInDateRange ? state.dateStart : null,
      previousDateEnd: state.isFirstClickInDateRange ? state.dateEnd: null,
      isFirstClickInDateRange: !state.isFirstClickInDateRange
    });

    if (!state.isFirstClickInDateRange) {
      onRangeSelect(dateStart, dateEnd, "months");
      onClose();
    }
  }

  function handleDayClick(selectWeeks: boolean, date:dayjs.Dayjs) {
    let dateStart, dateEnd;
    if (state.isFirstClickInDateRange) {
      dateStart = date;
      dateEnd = date;
    } else if (date.isAfter(state.dateEnd)){
      dateStart = state.dateStart;
      dateEnd = date;
    } else {
      dateStart = date;
      dateEnd = state.dateEnd;
    }

    if ( selectWeeks ) {
      dateStart = dateStart.startOf("isoWeek");
      dateEnd = dateEnd.endOf("isoWeek");
    }

    setState({
      ...state,
      previousDateStart: state.isFirstClickInDateRange ? state.dateStart : null,
      previousDateEnd: state.isFirstClickInDateRange ? state.dateEnd: null,
      dateStart: dateStart,
      dateEnd: dateEnd,
      isFirstClickInDateRange: !state.isFirstClickInDateRange
    });

    if (!state.isFirstClickInDateRange) {
      onRangeSelect(dateStart, dateEnd, state.dateMode);
      onClose();
    }
  }

  function addPeriodForMode(amount:number) {
    if (state.dateMode === "days" || state.dateMode === "weeks") {
      setState({
        ...state,
        month1: dayjs.utc(state.month1).add(amount, "months"),
        month2: dayjs.utc(state.month2).add(amount, "months")
      });
    } else if ( state.dateMode === "months" ) {
      setState({
        ...state,
        year1: dayjs.utc(state.year1).add(amount, "years"),
        year2: dayjs.utc(state.year2).add(amount, "years")
      });
    }
  }

  function prevMonthOrYear() {
    addPeriodForMode(-1)
  }

  function nextMonthOrYear() {
    addPeriodForMode(1);
  }

  function setHover(enableHover:boolean, date:dayjs.Dayjs): void {
    setState({
      ...state,
      hover: enableHover ? date : null
    });
  }

  function handleModeViewChange(mode:DateModes) {
    setState({
      ...state,
      dateMode: mode,
      dateStart: state.previousDateStart ? state.previousDateStart : state.dateStart,
      dateEnd: state.previousDateEnd ? state.previousDateEnd : state.dateEnd,
      isFirstClickInDateRange: true
    });
  }

  React.useEffect(() => {
    const handleClickOutside = (event:any) => {
      if (ref.current && !ref.current.contains(event.target) && onClickOutside) {
        onClickOutside();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ onClickOutside ]);

  return (
    <div className="daterangepicker" ref={ref}>
        <Accordion activeKey={state.dateMode}>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey="days" onClick={() => handleModeViewChange("days")}>
            Days
            {state.dateMode === "days" && 
              <span className="accordion-arrow accordion-arrow-right"></span>
            }
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="days">
            <Card.Body>
              <ul className="shortcuts">
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.today)}>Today</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.yesterday)}>Yesterday</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.last7Days)}>Last 7 Days</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.last14Days)}>Last 14 Days</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.last30Days)}>Last 30 Days</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.thisMonth)}>This Month ({state.thisMonth[0].format("YYYY-MMM")})</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("days", state.lastMonth)}>Last Month ({state.lastMonth[0].format("YYYY-MMM")})</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey="weeks" onClick={() => handleModeViewChange("weeks")}>
            Weeks
            {state.dateMode === "weeks" && 
              <span className="accordion-arrow accordion-arrow-right"></span>
            }
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="weeks">
            <Card.Body>
              <ul className="shortcuts">
                <li className="shortcut" onClick={()=>handleFixedRangeClick("weeks", state.thisWeek)}>This Week</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("weeks", state.lastWeek)}>Last Week</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("weeks", state.thisMonthWeeks)}>This Month (weeks)</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("weeks", state.lastMonthWeeks)}>Last Month (weeks)</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("weeks", state.last12Weeks)}>Last 12 Weeks</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey="months" onClick={() => handleModeViewChange("months")}>
            Months
            {state.dateMode === "months" && 
              <span className="accordion-arrow accordion-arrow-right"></span>
            }
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="months">
            <Card.Body>
              <ul className="shortcuts">
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.thisMonth)}>This Month ({state.thisMonth[0].format("YYYY-MMM")})</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.lastMonth)}>Last Month ({state.lastMonth[0].format("YYYY-MMM")})</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.thisYear)}>This Year ({state.thisYear[0].format("YYYY")})</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.lastYear)}>Last Year ({state.lastYear[0].format("YYYY")})</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.last6Months)}>Last 7 months</li>
                <li className="shortcut" onClick={()=>handleFixedRangeClick("months", state.last12Months)}>Last 13 months</li>
              </ul>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      <div className="calendars">
        {state.dateMode === "days" || state.dateMode === "weeks" ? (
            <>
              <DayCalendar
                id="day-calendar-1"
                key="day-calendar-1"
                month={state.month1}
                dateStart={state.dateStart}
                dateEnd={state.dateEnd} 
                isFirstClickInDateRange={state.isFirstClickInDateRange}
                hover={state.hover}
                minDate={state.minDate}
                setHover={setHover}
                prevMonthOrYear={prevMonthOrYear}
                handleDayClick={(date:any)=>handleDayClick(state.dateMode === "weeks", date)}
                selectWeeks={state.dateMode === "weeks"}
              />
              <DayCalendar
                id="day-calendar-2"
                key="day-calendar-2"
                month={state.month2}
                dateStart={state.dateStart}
                dateEnd={state.dateEnd}
                isFirstClickInDateRange={state.isFirstClickInDateRange}
                hover={state.hover}
                maxDate={state.maxDate}
                setHover={setHover}
                nextMonthOrYear={nextMonthOrYear}
                handleDayClick={(date:any)=>handleDayClick(state.dateMode === "weeks", date)}
                selectWeeks={state.dateMode === "weeks"}
              />
            </>
          ) : null}
          {state.dateMode === "months" ? (
            <>
              <MonthCalendar
                id="month-calendar-1"
                key="month-calendar-1"
                year={state.year1}
                dateStart={state.dateStart}
                dateEnd={state.dateEnd}
                isFirstClickInDateRange={state.isFirstClickInDateRange}
                hover={state.hover}
                minDate={state.minDate}
                setHover={setHover}
                prevMonthOrYear={prevMonthOrYear}
                handleMonthClick={(dateStart:any, dateEnd:any)=>handleMonthClick(dateStart, dateEnd)}
              />
              <MonthCalendar
                id="month-calendar-2"
                key="month-calendar-2"
                year={state.year2}
                dateStart={state.dateStart}
                dateEnd={state.dateEnd}
                isFirstClickInDateRange={state.isFirstClickInDateRange}
                hover={state.hover}
                maxDate={state.maxDate}
                setHover={setHover}
                nextMonthOrYear={nextMonthOrYear}
                handleMonthClick={(dateStart:any, dateEnd:any)=>handleMonthClick(dateStart, dateEnd)}
              />
            </>
          ) : null}
      </div>
    </div>
  );
}

export default DateRangePicker;